import { BUG_SCREENSHOT_MAX_BYTES, bugPagePath, type BugReportInput } from "@wmf/shared/bugReports";

export function bugPageContext(): Pick<BugReportInput, "pagePath" | "pageTitle" | "client"> {
  const asset = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src;
  const path = asset ? new URL(asset, window.location.href).pathname : "";
  let timezone = "";
  try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* browser default */ }
  return { pagePath: bugPagePath(window.location.pathname + window.location.search), pageTitle: document.title.slice(0, 200),
    client: {
      viewportWidth: window.innerWidth, viewportHeight: window.innerHeight,
      screenWidth: window.screen.width || window.innerWidth, screenHeight: window.screen.height || window.innerHeight,
      pixelRatio: window.devicePixelRatio || 1, language: navigator.language.slice(0, 50), timezone,
      online: navigator.onLine, buildAsset: /^\/assets\/[a-zA-Z0-9_.-]+\.js$/.test(path) ? path : "",
    } };
}

/** Redact editable fields in the detached copy. The user's page is never edited. */
export function redactBugCapture(document: Document): void {
  // Native closed details use browser layout rules that DOM renderers can
  // otherwise miss. Keep their summaries, without rendering hidden content.
  document.querySelectorAll("details:not([open])").forEach(details => {
    const summary = details.querySelector(":scope > summary");
    for (const child of [...details.childNodes]) if (child !== summary) child.remove();
  });
  document.querySelectorAll("input,textarea,select,[contenteditable], [data-bug-report-private]").forEach(element => {
    if (element.tagName === "INPUT" && element.getAttribute("type") === "hidden") { element.remove(); return; }
    const placeholder = document.createElement("span");
    placeholder.textContent = "[Hidden]";
    placeholder.style.cssText = "display:inline-block;background:#ddd;color:#333;padding:8px;border-radius:4px;min-width:60px";
    element.replaceWith(placeholder);
  });
}

async function pngFile(canvas: HTMLCanvasElement): Promise<File> {
  for (let attempt = 0; attempt < 4; attempt++) {
    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, "image/png"));
    if (blob && blob.size <= BUG_SCREENSHOT_MAX_BYTES) return new File([blob], "hub-page.png", { type: "image/png" });
    const smaller = document.createElement("canvas");
    smaller.width = Math.max(1, Math.floor(canvas.width * .7));
    smaller.height = Math.max(1, Math.floor(canvas.height * .7));
    const context = smaller.getContext("2d");
    if (!context) break;
    context.drawImage(canvas, 0, 0, smaller.width, smaller.height);
    canvas = smaller;
  }
  throw new Error("The screenshot is too large. You can send the report without an image.");
}

export async function captureBugPage(): Promise<File> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const controller = new AbortController();
  try {
    return await Promise.race([
      (async () => {
        const { default: html2canvas } = await import("html2canvas-pro");
        const canvas = await html2canvas(document.body, {
          x: window.scrollX, y: window.scrollY, width: window.innerWidth, height: window.innerHeight,
          windowWidth: window.innerWidth, windowHeight: window.innerHeight,
          scale: Math.min(1.5, 2400 / Math.max(window.innerWidth, window.innerHeight)),
          allowTaint: false, useCORS: false, imageTimeout: 2000, logging: false,
          signal: controller.signal,
          onclone: redactBugCapture,
          ignoreElements: element => element.matches("iframe,video,[hidden],[data-bug-report-ignore]") || Boolean(element.closest("[data-bug-report-ignore]")),
        });
        return pngFile(canvas);
      })(),
      new Promise<never>((_resolve, reject) => { timer = setTimeout(() => {
        controller.abort(); reject(new Error("The screenshot could not be captured. You can add an image or send the report without one."));
      }, 10000); }),
    ]);
  } finally { clearTimeout(timer); }
}

/** Re-encoding also removes photo metadata from a manually selected image. */
export async function prepareBugImage(file: File): Promise<File> {
  if (!/^image\/(png|jpeg|webp)$/.test(file.type) || file.size > 10 * 1024 * 1024) throw new Error("Choose a PNG, JPEG or WebP image smaller than 10 MB.");
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => { image.onload = () => resolve(); image.onerror = () => reject(new Error("This image could not be opened.")); image.src = url; });
    if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth * image.naturalHeight > 40_000_000) throw new Error("Choose a smaller screenshot.");
    const scale = Math.min(1, 2400 / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale)); canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Images are unavailable in this browser.");
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return await pngFile(canvas);
  } finally { URL.revokeObjectURL(url); }
}
